import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ArrowBold from '../../../assets/svgs/solar_alt-arrow-up-bold.svg';
import Check from '../../../assets/svgs/ph_check-bold.svg';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

// Gap between the trigger and the open menu.
const MENU_OFFSET = 8;
// Never let the menu run to the very edge of the viewport.
const VIEWPORT_MARGIN = 8;

export default function CameraPermissionSelect({
  list,
  icon,
  placeholder,
  setSelectedItem,
  defaultValue,
  noDataText,
  disabled,
}) {
  const { t } = useTranslation('camera-permission');
  const [selectedValue, setSelectedValue] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  const cameraRef = useRef(null);
  const menuRef = useRef(null);

  // The menu is portalled to <body> and positioned against the trigger's
  // viewport rect. It used to be an absolutely-positioned child, which meant any
  // ancestor with overflow-hidden clipped it — on portrait mobile the camera
  // column is a fixed 70% grid row, so the menu was cut off and read as "the
  // dropdown doesn't open". A portal has no ancestor to be clipped by.
  const updateMenuPosition = useCallback(() => {
    const trigger = cameraRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const spaceAbove = rect.top - MENU_OFFSET - VIEWPORT_MARGIN;
    const spaceBelow = window.innerHeight - rect.bottom - MENU_OFFSET - VIEWPORT_MARGIN;
    // Prefer opening upward (the original behaviour, and on this screen there's
    // usually more room over the preview than under the copy), but flip down
    // when that's genuinely the roomier side.
    const openUpward = spaceAbove >= spaceBelow;

    setMenuPosition({
      left: rect.left,
      width: rect.width,
      openUpward,
      maxHeight: Math.max(120, openUpward ? spaceAbove : spaceBelow),
      top: openUpward ? undefined : rect.bottom + MENU_OFFSET,
      bottom: openUpward ? window.innerHeight - rect.top + MENU_OFFSET : undefined,
    });
  }, []);

  useEffect(() => {
    if (!openMenu) {
      setMenuPosition(null);
      return undefined;
    }

    updateMenuPosition();

    // touchstart alongside mousedown: on a touch device mousedown only arrives
    // after the gesture finishes, so without it a tap outside felt unresponsive.
    const handleDismiss = event => {
      if (cameraRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }
      setOpenMenu(false);
    };

    // Capture phase + `true` on scroll so the menu tracks the trigger even when
    // an inner scroll container (not the window) is the thing moving.
    document.addEventListener('mousedown', handleDismiss);
    document.addEventListener('touchstart', handleDismiss);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      document.removeEventListener('mousedown', handleDismiss);
      document.removeEventListener('touchstart', handleDismiss);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [openMenu, updateMenuPosition]);

  // Initialize selectedValue with defaultValue
  useEffect(() => {
    if (defaultValue) {
      const defaultItem = list.find(item => item.value === defaultValue.value);
      setSelectedValue(defaultItem);
    }
  }, [defaultValue, list]);

  const handleSelectedValue = item => {
    setSelectedValue(item);
    setSelectedItem(item.value);
  };

  const handleToggleMenu = () => {
    if (disabled) {
      return;
    }
    setOpenMenu(prev => !prev);
  };

  const handleClickOnListItem = item => {
    handleSelectedValue(item);
    setOpenMenu(false);
  };

  // Stop event propagation on menu item click to prevent closing menu
  const handleMenuItemClick = (event, item) => {
    event.stopPropagation();
    handleClickOnListItem(item);
  };

  const menu =
    openMenu && menuPosition && list.length > 0
      ? createPortal(
          <ul
            ref={menuRef}
            tw="fixed z-[10000] overflow-y-auto bg-white grid gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.18)] rounded-[8px] py-1"
            style={{
              left: menuPosition.left,
              width: menuPosition.width,
              top: menuPosition.top,
              bottom: menuPosition.bottom,
              maxHeight: menuPosition.maxHeight,
            }}
            onClick={e => e.stopPropagation()}
          >
            {list.map(item => (
              <li
                key={item.label}
                // min-h-[44px]: these are finger targets on the phone this
                // dropdown mostly runs on.
                tw="px-[5px] py-[12px] min-h-[44px] flex items-center cursor-pointer"
                css={item.value === selectedValue?.value ? tw`text-[#8DA12B]` : tw``}
                onClick={e => handleMenuItemClick(e, item)}
              >
                {item.value === selectedValue?.value && (
                  <img loading="lazy" decoding="async" src={Check} alt="check" tw="me-4" />
                )}
                <p tw="text-[14px]" css={item.value === selectedValue?.value ? tw`` : tw`ms-8`}>
                  {item.label}
                </p>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div tw="relative w-full" ref={cameraRef}>
      {menu}
      <div
        className="select-item"
        onClick={handleToggleMenu}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={openMenu}
        // min-h-[44px] so the trigger itself is a comfortable tap target.
        // rounded/padding below md match Figma's mobile "Camera permission -
        // Disabled" frame; md and up keep the original values untouched.
        tw="border-[1px] border-[#BDC4C9] rounded-[12px] max-md:rounded-[6px] flex items-center justify-between w-full min-h-[44px] px-[2%] py-[8px] max-md:px-[12px] max-md:py-[10px]"
        css={
          disabled
            ? tw`bg-[#F5F5F5] cursor-not-allowed opacity-60`
            : list.length === 0
              ? tw`bg-[#F5F5F5] cursor-not-allowed`
              : tw`cursor-pointer`
        }
      >
        <div tw="flex gap-2 max-md:gap-3 min-w-0 items-center">
          {icon && (
            <img
              loading="lazy"
              decoding="async"
              width={20}
              tw="max-md:w-[26px] max-md:shrink-0"
              src={icon}
              alt="speaker"
            />
          )}
          {list.length > 0 && (
            <p tw="text-[12px] truncate max-md:text-[16px] max-md:font-medium max-md:text-[#656565]">
              {selectedValue ? selectedValue.label : placeholder}
            </p>
          )}
          {list.length === 0 && (
            <p tw="text-[12px] truncate max-md:text-[16px] max-md:font-medium max-md:text-[#656565]">
              {noDataText}
            </p>
          )}
        </div>
        {list.length > 0 && (
          <img
            loading="lazy"
            decoding="async"
            src={ArrowBold}
            tw="shrink-0 transition-all duration-500"
            css={openMenu ? tw`rotate-180` : tw``}
            alt={t('select.altArrowSelect')}
          />
        )}
      </div>
    </div>
  );
}
